import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Badge, Tabs, Grid, StatCard } from '../../ui/components';
import { Users, Clock, CircleCheck, TrendingUp, DollarSign, BarChart3, Copy, Share2 } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';
import { supabase } from '../../lib/supabase';
import { AppLayout } from '../../components/layout';
import { REFERRAL_NAV } from '../../config/navigation';

const statusColors = {
  clicked: 'neutral',
  started: 'primary',
  in_progress: 'warning',
  submitted: 'primary',
  approved: 'success',
  funded: 'success',
  withdrawn: 'danger',
};

const statusLabels = {
  clicked: 'Link Clicked',
  started: 'App Started',
  in_progress: 'In Progress',
  submitted: 'Submitted',
  approved: 'Approved',
  funded: 'Funded',
  withdrawn: 'Withdrawn',
};

const ReferralPartnerDashboard = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    inProgress: 0,
    successful: 0,
    conversionRate: 0,
    totalLoanVolume: 0,
    avgReadinessScore: 0,
  });
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const session = localStorage.getItem('lk_partner_session');
    if (!session) {
      navigate('/partner/login');
      return;
    }
    
    const partnerData = JSON.parse(session);
    setPartner(partnerData);
    fetchPartnerData(partnerData.id);
  }, [navigate]);

  const fetchPartnerData = async (partnerId) => {
    setLoading(true);
    try {
      const { data: partnerDetails } = await supabase
        .from('referral_partners')
        .select('*')
        .eq('id', partnerId)
        .single();

      if (partnerDetails) {
        setPartner(prev => ({ ...prev, ...partnerDetails }));
      }

      const { data: referralsData } = await supabase
        .from('referrals')
        .select('*, loan_applications(id, status, loan_amount, readiness_score, created_at)')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });

      if (referralsData) {
        setReferrals(referralsData);
        
        const successful = referralsData.filter(r => ['approved', 'funded'].includes(r.status)).length;
        const inProgress = referralsData.filter(r => ['started', 'in_progress', 'submitted'].includes(r.status)).length;
        const totalLoanVolume = referralsData
          .filter(r => r.loan_applications?.loan_amount)
          .reduce((sum, r) => sum + (r.loan_applications?.loan_amount || 0), 0);
        const scoresWithValues = referralsData.filter(r => r.loan_applications?.readiness_score);
        const avgScore = scoresWithValues.length > 0
          ? Math.round(scoresWithValues.reduce((sum, r) => sum + r.loan_applications.readiness_score, 0) / scoresWithValues.length)
          : 0;

        setStats({
          totalReferrals: referralsData.length,
          inProgress,
          successful,
          conversionRate: referralsData.length > 0 ? Math.round((successful / referralsData.length) * 100) : 0,
          totalLoanVolume,
          avgReadinessScore: avgScore,
        });
      }
    } catch (error) {
      console.error('Error fetching partner data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('lk_partner_session');
    navigate('/partner/login');
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(partner?.referral_code || '');
    alert('Referral code copied!');
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/apply?ref=${partner?.referral_code}`;
    navigator.clipboard.writeText(link);
    alert('Referral link copied!');
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (!partner) return null;

  return (
    <AppLayout
      role="referral"
      navItems={REFERRAL_NAV}
      user={{
        name: `${partner.firstName} ${partner.lastName}`,
        email: partner.email,
        role: 'referral',
      }}
      onLogout={handleLogout}
    >
      <div>
        {/* Partner Info & Referral Code */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--lk-space-4)', marginBottom: 'var(--lk-space-4)' }}>
          <Surface padding="var(--lk-space-4)">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--lk-primary-100)', color: 'var(--lk-primary-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 700, flexShrink: 0 }}>
                {partner.firstName?.[0]}{partner.lastName?.[0]}
              </div>
              <div>
                <h3 style={{ fontWeight: 700, margin: '0 0 4px' }}>{partner.firstName} {partner.lastName}</h3>
                <div style={{ color: 'var(--lk-text-tertiary)' }}>{partner.companyName || partner.email}</div>
                <Badge variant="primary" style={{ marginTop: 'var(--lk-space-2)' }}>
                  {partner.partnerType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>
            </div>
          </Surface>
          <Surface padding="var(--lk-space-4)">
            <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', marginBottom: 'var(--lk-space-2)' }}>Your Referral Code</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 'var(--lk-text-xl)' }}>{partner.referral_code || partner.referralCode}</span>
              <Button size="sm" variant="ghost" icon={Copy} onClick={copyReferralCode} />
            </div>
            <Button variant="primary" size="sm" icon={Share2} onClick={copyReferralLink} style={{ width: '100%' }}>
              Copy Referral Link
            </Button>
          </Surface>
        </div>

        {/* Stats Cards */}
        <Grid cols={6} gap="var(--lk-space-3)" style={{ marginBottom: 'var(--lk-space-4)' }}>
          <StatCard label="Total Referrals" value={stats.totalReferrals} icon={Users} />
          <StatCard label="In Progress" value={stats.inProgress} icon={Clock} />
          <StatCard label="Successful" value={stats.successful} icon={CircleCheck} />
          <StatCard label="Conversion" value={`${stats.conversionRate}%`} icon={TrendingUp} />
          <StatCard label="Loan Volume" value={`$${(stats.totalLoanVolume / 1000000).toFixed(1)}M`} icon={DollarSign} />
          <StatCard label="Avg Score" value={stats.avgReadinessScore} icon={BarChart3} />
        </Grid>

        {/* Referrals Table */}
        <Surface padding="0" style={{ marginBottom: 'var(--lk-space-4)' }}>
          <div style={{ padding: 'var(--lk-space-4) var(--lk-space-5)', borderBottom: '1px solid var(--lk-border-subtle)' }}>
            <h5 style={{ fontWeight: 700, margin: 0 }}>Your Referrals</h5>
          </div>
          <div style={{ padding: 'var(--lk-space-3) var(--lk-space-5) 0' }}>
            <Tabs
              tabs={[
                { id: 0, label: `All (${referrals.length})` },
                { id: 1, label: `Active (${referrals.filter(r => ['started', 'in_progress', 'submitted'].includes(r.status)).length})` },
                { id: 2, label: `Completed (${referrals.filter(r => ['approved', 'funded'].includes(r.status)).length})` },
              ]}
              activeTab={tabValue}
              onChange={setTabValue}
            />
          </div>

          {loading ? (
            <div style={{ padding: 'var(--lk-space-3)' }}>
              <div style={{ height: 4, background: 'var(--lk-primary-500)', animation: 'lk-progress 1.5s infinite' }} />
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="lk-table">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Loan Amount</th>
                    <th>Readiness Score</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals
                    .filter(r => {
                      if (tabValue === 1) return ['started', 'in_progress', 'submitted'].includes(r.status);
                      if (tabValue === 2) return ['approved', 'funded'].includes(r.status);
                      return true;
                    })
                    .map((referral) => {
                      const progress =
                        referral.status === 'clicked' ? 10 :
                        referral.status === 'started' ? 25 :
                        referral.status === 'in_progress' ? 50 :
                        referral.status === 'submitted' ? 75 :
                        referral.status === 'approved' ? 90 :
                        referral.status === 'funded' ? 100 : 0;
                      const isComplete = ['approved', 'funded'].includes(referral.status);

                      return (
                        <tr key={referral.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--lk-primary-100)', color: 'var(--lk-primary-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--lk-text-xs)', fontWeight: 600, flexShrink: 0 }}>
                                {referral.applicant_name?.[0] || referral.applicant_email?.[0] || '?'}
                              </div>
                              <div>
                                <div style={{ fontWeight: 500 }}>{referral.applicant_name || 'Anonymous'}</div>
                                <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{referral.applicant_email || 'No email provided'}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>{new Date(referral.created_at).toLocaleDateString()}</div>
                            <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{getTimeAgo(referral.created_at)}</div>
                          </td>
                          <td>
                            <Badge variant={statusColors[referral.status] || 'neutral'}>
                              {statusLabels[referral.status] || referral.status}
                            </Badge>
                          </td>
                          <td>
                            {referral.loan_applications?.loan_amount
                              ? `$${referral.loan_applications.loan_amount.toLocaleString()}`
                              : '-'}
                          </td>
                          <td>
                            {referral.loan_applications?.readiness_score ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                                <span style={{ fontWeight: 600 }}>{referral.loan_applications.readiness_score}</span>
                                <div style={{ width: 60, height: 6, borderRadius: 3, background: 'var(--lk-gray-100)' }}>
                                  <div style={{ height: '100%', width: `${referral.loan_applications.readiness_score}%`, borderRadius: 3, background: 'var(--lk-primary-500)' }} />
                                </div>
                              </div>
                            ) : '-'}
                          </td>
                          <td>
                            <div style={{ width: 100, height: 8, borderRadius: 4, background: 'var(--lk-gray-100)' }}>
                              <div style={{ height: '100%', width: `${progress}%`, borderRadius: 4, background: isComplete ? 'var(--lk-success-500)' : 'var(--lk-primary-500)', transition: 'width 0.4s ease' }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  {referrals.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 'var(--lk-space-8)' }}>
                        <div style={{ color: 'var(--lk-text-tertiary)', marginBottom: 'var(--lk-space-3)' }}>
                          No referrals yet. Share your referral link to get started!
                        </div>
                        <Button variant="primary" icon={Share2} onClick={copyReferralLink}>
                          Copy Referral Link
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Surface>

        {/* Tips Section */}
        <Surface padding="var(--lk-space-5)">
          <h5 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-3)' }}>Tips for Success</h5>
          <Grid cols={3} gap="var(--lk-space-3)">
            <Surface padding="var(--lk-space-4)">
              <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-2)' }}>Email Your Clients</div>
              <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
                Send your referral link directly to clients who are ready to explore mortgage options.
              </div>
            </Surface>
            <Surface padding="var(--lk-space-4)">
              <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-2)' }}>Add to Your Website</div>
              <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
                Include your referral link on your website or email signature for passive referrals.
              </div>
            </Surface>
            <Surface padding="var(--lk-space-4)">
              <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-2)' }}>Explain the Benefits</div>
              <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
                Let clients know they'll get pre-underwritten and see their mortgage readiness before talking to lenders.
              </div>
            </Surface>
          </Grid>
        </Surface>
      </div>

      <FloatingOwenAssistant context="referral-dashboard" />
      <FloatingKnowledge customContent={{
        label: 'Referrals',
        title: 'Partner dashboard',
        body: 'Track your referral performance and earnings.',
        bullets: ['Monitor referral conversions', 'Track commission earnings', 'Optimize referral quality'],
        resources: [{ label: 'Partner program guide', href: '#' }],
      }} />
    </AppLayout>
  );
};

export default ReferralPartnerDashboard;
